import React, { useEffect, useMemo, useState } from "react";
import { qrtServiceUsage } from "../../apis/qrtAdmin";
import { Pager, Spinner, StatCard, fmtMoney, num } from "./qrtUi";

const PAGE_SIZE = 50;

function istIso(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

function shiftIso(iso, days) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

function prettyDate(iso) {
  const [y, m, d] = String(iso || "").split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (Number.isNaN(dt.getTime())) return iso || "";
  return dt.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

function monthLabel(key) {
  const [y, m] = String(key || "").split("-");
  const d = new Date(Date.UTC(Number(y), Number(m) - 1, 1));
  if (Number.isNaN(d.getTime())) return key || "";
  return d.toLocaleString("en-GB", { month: "short", year: "numeric", timeZone: "UTC" });
}

function rangeFor(preset) {
  const today = istIso();
  if (preset === "today") return { from: today, to: today };
  if (preset === "yesterday") {
    const y = shiftIso(today, -1);
    return { from: y, to: y };
  }
  if (preset === "month") return { from: `${today.slice(0, 8)}01`, to: today };
  if (preset === "last_month") {
    const firstThis = `${today.slice(0, 8)}01`;
    const last = shiftIso(firstThis, -1);
    return { from: `${last.slice(0, 8)}01`, to: last };
  }
  return { from: `${today.slice(0, 8)}01`, to: today };
}

const PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "custom", label: "Custom" },
];

function counted(n, one, many) {
  const v = Number(n) || 0;
  return `${num(v)} ${v === 1 ? one : many}`;
}

// Meta (1 Oct 2026): free-form service messages get a monthly free allowance
// per WhatsApp number, then bill at the utility rate. This page shows usage
// per user + number, and what we earned on the overage.
const QrtServiceUsage = () => {
  const initial = rangeFor("month");
  const [preset, setPreset] = useState("month");
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [query, setQuery] = useState("");
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      qrtServiceUsage({
        from,
        to,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        ...(query.trim() ? { q: query.trim() } : {}),
      })
        .then((res) => setData(res.data?.data || null))
        .catch(() => setData(null))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [from, to, query, page]);

  const applyPreset = (id) => {
    setPreset(id);
    setPage(0);
    if (id === "custom") return;
    const next = rangeFor(id);
    setFrom(next.from);
    setTo(next.to);
  };

  const grouped = useMemo(() => {
    const out = [];
    for (const row of data?.rows || []) {
      let group = out.find((g) => g.id === row.platform_account_id);
      if (!group) {
        group = { id: row.platform_account_id, rows: [] };
        out.push(group);
      }
      group.rows.push(row);
    }
    return out;
  }, [data]);

  if (!data && loading) return <Spinner />;
  if (!data) return <div className="qrt-card"><p className="empty">Failed to load service usage</p></div>;

  const t = data.totals || {};
  const rangeLabel = data.from === data.to
    ? prettyDate(data.from)
    : `${prettyDate(data.from)} – ${prettyDate(data.to)}`;

  return (
    <div>
      <div className="qrt-page-head">
        <div>
          <h2>Service messages</h2>
          <p>
            First {num(data.allowance)} free-form replies per WhatsApp number each month, then the utility rate
          </p>
        </div>
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head">
          <div className="qrt-range-picks" style={{ margin: 0 }}>
            {PRESETS.map((p) => (
              <button
                key={p.id}
                type="button"
                className={preset === p.id ? "on" : ""}
                onClick={() => applyPreset(p.id)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="qrt-inline">
            <label style={{ fontSize: 12 }}>
              From{" "}
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => {
                  setPreset("custom");
                  setPage(0);
                  if (e.target.value) setFrom(e.target.value);
                }}
              />
            </label>
            <label style={{ fontSize: 12 }}>
              To{" "}
              <input
                type="date"
                value={to}
                min={from}
                onChange={(e) => {
                  setPreset("custom");
                  setPage(0);
                  if (e.target.value) setTo(e.target.value);
                }}
              />
            </label>
          </div>
          <input
            type="search"
            placeholder="Search user, email or number"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
          />
        </div>
      </div>

      <div className="qrt-stats">
        <StatCard
          label="Messages sent"
          value={num(t.total_used)}
          sub={`${counted(t.total_failed, "failed, not counted", "failed, not counted")} · ${counted(t.total_numbers, "number active", "numbers active")}`}
        />
        <StatCard label="Charged messages" value={num(t.total_charged)} sub={counted(t.numbers_over_allowance, "number over the free allowance", "numbers over the free allowance")} />
        <StatCard label="Billed to users" value={fmtMoney(t.total_revenue)} sub={`Meta cost ${fmtMoney(t.total_cost)}`} tone="blue" />
        <StatCard label="Profit" value={fmtMoney(t.total_profit)} />
      </div>

      <div className="qrt-card">
        <div className="qrt-card-head">
          <h3>Usage by number</h3>
          <span className="sub" style={{ fontSize: 12 }}>
            {rangeLabel}. Sent and failed are for these dates. A failed message does not use the free allowance. Free left is what remains of the {num(data.allowance)} for {monthLabel(data.allowance_month)}.
          </span>
        </div>
        {loading ? <Spinner /> : (
          <table className="qrt-table">
            <thead>
              <tr>
                <th>User</th>
                <th>WhatsApp number</th>
                <th className="r">Sent</th>
                <th className="r">Failed</th>
                <th className="r">Free left</th>
                <th className="r">Charged msgs</th>
                <th className="r">Billed</th>
                <th className="r">Meta cost</th>
                <th className="r">Profit</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) => group.rows.map((r, i) => (
                <tr key={`${r.platform_account_id}-${r.phone_number_id}`}>
                  {i === 0 && (
                    <td rowSpan={group.rows.length}>
                      <div style={{ fontWeight: 600 }}>{r.user_name || `#${r.platform_account_id}`}</div>
                      <div className="sub">{r.user_company || r.user_email || ""}</div>
                    </td>
                  )}
                  <td>
                    <div className="mono">{r.phone_number || r.phone_number_id}</div>
                    <div className="sub">{r.verified_name || ""}</div>
                  </td>
                  <td className="r mono">{num(r.used_count)}</td>
                  <td className="r mono" style={r.failed_count > 0 ? { color: "#c0392b" } : undefined}>{num(r.failed_count)}</td>
                  <td className="r mono" style={r.free_remaining === 0 ? { color: "#c0392b", fontWeight: 600 } : undefined}>
                    {num(r.free_remaining)}
                  </td>
                  <td className="r mono">{num(r.charged_count)}</td>
                  <td className="r mono">{fmtMoney(r.charged_amount)}</td>
                  <td className="r mono sub">{fmtMoney(r.base_amount)}</td>
                  <td className="r mono" style={{ fontWeight: 600 }}>{fmtMoney(r.profit)}</td>
                </tr>
              )))}
              {grouped.length === 0 && (
                <tr><td className="empty" colSpan={9}>No service messages from {rangeLabel}</td></tr>
              )}
            </tbody>
          </table>
        )}
        <Pager page={page} total={Number(t.total_numbers) || 0} pageSize={PAGE_SIZE} onPage={setPage} />
      </div>
    </div>
  );
};

export default QrtServiceUsage;
